import { Module } from 'vuex';
import { IRootState } from '..';
import setData from '../setData';

import { IDeviceIdConfiguration, IDocumentVerificationConfiguration } from '@/api/configuration';
import { configurationAPI, documentVerificationAPI, auditAPI } from '@/api';

export interface IIdentityVerificationState {
	deviceIdEnabled: boolean;
	deviceIdSessionToken: string;
	deviceIdSDKKey: string;
	documentVerificationEnabled: boolean;
}

const personVerification: Module<IIdentityVerificationState, IRootState> = {
	namespaced: true,
	state: {
		deviceIdEnabled: false,
		deviceIdSessionToken: '',
		deviceIdSDKKey: '',
		documentVerificationEnabled: false,
	},
	mutations: {
		setData,
		setPersonVerificationConfiguration(state, data: IDeviceIdConfiguration) {
			state.deviceIdEnabled = data.enabled;
			state.deviceIdSDKKey = data.sdkKey;
		},
		setDocumentVerificationConfiguration(state, data: IDocumentVerificationConfiguration) {
			state.documentVerificationEnabled = data.enabled;
		},
		resetState(state) {
			state.deviceIdEnabled = false,
			state.deviceIdSessionToken = '',
			state.deviceIdSDKKey = '',
			state.documentVerificationEnabled = false
		}
	},
	actions: {
		async initDeviceId({ commit }): Promise<void> {
			try {
				const setPersonVerificationConfiguration = await configurationAPI.getDeviceIdConfiguration();
				commit('setPersonVerificationConfiguration', setPersonVerificationConfiguration);
			} catch (error) {
				throw error
			}
		},
		async initDocumentVerificationModule({ commit }): Promise<void> {
			try {
				const docVConfig = await configurationAPI.getDocumentVerificationConfiguration();
				commit('setDocumentVerificationConfiguration', docVConfig);
			} catch (error) {
				throw error
			}
		},
		attachDeviceIdSDK({ state, dispatch }): void {
			try {
				if (state.deviceIdEnabled && !hasSigmaDeviceManager()) {
					//place deviceId script on document header
					let deviceIdScript = document.createElement('script');
					deviceIdScript.src = 'https://cdn.dvnfo.com/device-risk-sdk.js';
					deviceIdScript.setAttribute('data-public-key', state.deviceIdSDKKey);

					//get session id when loaded
					deviceIdScript.addEventListener('load', () => {
						dispatch('refreshDeviceIdSessionToken');
					});

					document.head.appendChild(deviceIdScript);
				}
			} catch (error) {
				throw error
			}
		},
		async refreshDeviceIdSessionToken({ state, commit }): Promise<string> {
			let sessionToken = '';
			let updateClientAudit = !state.deviceIdSessionToken;
			const clientIp = (await auditAPI.getClientIp()) || '127.0.0.1';

			try {
				if (state.deviceIdEnabled && hasSigmaDeviceManager()) {
					// @ts-ignore because SigmaDeviceManager is an object from an external script
					sessionToken = await SigmaDeviceManager.getDeviceSessionId();
					commit('setData', {
						objName: 'deviceIdSessionToken',
						data: sessionToken
					});

					if (updateClientAudit) {
						auditAPI.updateClientAuditAsync({
							deviceSessionId: sessionToken,
							clientIp: clientIp
						});
					}
				}
			} catch (error) {
				throw error
			}

			return sessionToken;
		},
		attachDocumentVerificationSDK({ state, dispatch }): void {
			try {
				if (state.documentVerificationEnabled) {
					//place deviceId script on document header
					let existingScript = document.querySelector("#docVSDK");
					if (!existingScript) {
						let documentVerificationScript = document.createElement('script');
						documentVerificationScript.id = "docVSDK";
						documentVerificationScript.src = 'https://websdk.socure.com/bundle.js';
						document.head.appendChild(documentVerificationScript);
					}
				}
			} catch (error) {
				throw error
			}
		}
	},
	getters: {
		deviceIdSessionToken: state => state.deviceIdSessionToken
	}
};

const hasSigmaDeviceManager = () => {
	// @ts-ignore because SigmaDeviceManager is an object from an external script
	return typeof SigmaDeviceManager !== 'undefined';
};

export default personVerification;
